const FLOW_LOCAL_BACKUP_KEY_PREFIX = 'flow-recording-backup:'

export type FlowLocalBackupKey = {
	teamId: TeamId
	seasonId: SeasonId
	gameId: GameId
}

export type FlowLocalBackup = {
	flow: FlowData
	signature: string
	savedAt: number
	lastAttemptedAt?: number | null
	lastError?: string | null
}

export function getFlowDataSignature(flow: FlowData | null | undefined) {
	if (!flow) {
		return null
	}

	return JSON.stringify([
		flow.setup
			? [
				flow.setup.firstCpTeam,
				flow.setup.homeAttackDirection,
				flow.setup.homeColor,
				flow.setup.awayColor,
			]
			: null,
		flow.currentQuarter || 1,
		flow.quarterBreaks || [ ],
		(flow.events || [ ]).map(event => [
			event.cpTeamCode,
			[ ...event.turnovers ],
			event.result,
		]),
		flow.draftEvent
			? [
				flow.draftEvent.cpTeamCode,
				[ ...flow.draftEvent.turnovers ],
			]
			: null,
	])
}

export function getLocalFlowBackupKey(key: FlowLocalBackupKey) {
	return `${FLOW_LOCAL_BACKUP_KEY_PREFIX}${key.teamId}:${key.seasonId}:${key.gameId}`
}

export function saveLocalFlowBackup(
	key: FlowLocalBackupKey,
	flow: FlowData,
	opt: {
		lastAttemptedAt?: number | null
		lastError?: string | null
	} = { },
) {
	try {
		const existing = loadLocalFlowBackup(key)
		const backup: FlowLocalBackup = {
			flow,
			signature: getFlowDataSignature(flow) || '',
			savedAt: Date.now(),
			lastAttemptedAt: 'lastAttemptedAt' in opt ? opt.lastAttemptedAt ?? null : existing?.lastAttemptedAt ?? null,
			lastError: 'lastError' in opt ? opt.lastError ?? null : existing?.lastError ?? null,
		}
		window.localStorage.setItem(getLocalFlowBackupKey(key), JSON.stringify(backup))
	} catch (error) {
		console.error('Failed to save local flow backup', error)
	}
}

export function loadLocalFlowBackup(key: FlowLocalBackupKey): FlowLocalBackup | null {
	try {
		const backup = window.localStorage.getItem(getLocalFlowBackupKey(key))
		if (!backup) {
			return null
		}

		const parsed = JSON.parse(backup) as FlowLocalBackup | FlowData
		if ('flow' in parsed) {
			return {
				...parsed,
				signature: getFlowDataSignature(parsed.flow) || '',
			}
		}

		return {
			flow: parsed,
			signature: getFlowDataSignature(parsed) || '',
			savedAt: parsed.updated || Date.now(),
			lastAttemptedAt: null,
			lastError: null,
		}
	} catch (error) {
		console.error('Failed to load local flow backup', error)
		return null
	}
}

export function removeLocalFlowBackup(key: FlowLocalBackupKey) {
	try {
		window.localStorage.removeItem(getLocalFlowBackupKey(key))
	} catch (error) {
		console.error('Failed to remove local flow backup', error)
	}
}
